import React, {useLayoutEffect, memo, useRef} from 'react';
import styled from 'styled-components';
import {useSelector} from 'react-redux';
import {createSelector} from 'reselect';
import {selectSsrRendered} from 'app/hooks/reduxCreateSelectorHooks';
import useDisableAds from 'app/hooks/useDisableAds';
import loadAdSlot from 'app/components/ads/loadAdSlot';
import {useLayout} from 'app/contexts/LayoutContext';
import {LayoutMode} from 'app/styles/LayoutMode';
import {WEB_INTERSTITIAL_SLOT_ID} from 'app/components/ads/ad.constants';

const Slot = styled.div`
  display: ${props => (props.id === 'ad-Sticky_Mobile_Banner' ? 'block' : 'flex')};
  align-items: inherit;
  justify-content: inherit;
  width: inherit;
  height: inherit;
  min-height: inherit;
  max-height: inherit;
  ${props => props.id === 'ad-Sticky_Mobile_Banner' && 'text-align: center;'};
`;

// Listen for AdsReady-Flag (QM)
const selectAdsReady = createSelector(
  state => state.session,
  session => session.adsReady
);

const destroyOldSlot = adUnitCode => {
  window.googletag.cmd.push(() => {
    const adSlotsOnPage = window.googletag.pubads().getSlots();
    const oldSlot = adSlotsOnPage.find(slot => slot.getAdUnitPath() === adUnitCode);
    if (oldSlot) {
      window.googletag.destroySlots([oldSlot]);
    }
  });
};

const callWebInterstital = adUnitCode => {
  window.googletag.cmd.push(() => {
    const slot = window.googletag.defineOutOfPageSlot(adUnitCode, window.googletag.enums.OutOfPageFormat.INTERSTITIAL);
    if (slot) {
      slot.addService(window.googletag.pubads());
      window.refreshBids([WEB_INTERSTITIAL_SLOT_ID], null, null, 1000, slot);
    }
  });
};

const defineAndDisplaySlot = (adUnitCode, sizes, id, prebidTimeout) => {
  // check for existing slots by layout change trigger
  window.googletag.cmd.push(() => {
    const slot = window.googletag.defineSlot(adUnitCode, sizes, id);
    slot.addService(window.googletag.pubads());

    // Prop 'sizes' is needed for test purposes to enable amazon bidder
    // Amazon bidder does not work with prebid
    // standalone solution is implemented and therefore sizes need to be injected
    window.refreshBids([id], adUnitCode, sizes, prebidTimeout, slot);
  });
};

const usePrebidTimeout = (id, isDesktop) => {
  const DESKTOP_TIMEOUT = 1000;
  const SSR_TIMEOUT = 1000;
  const CSR_DEFAULT_TIMEOUT = 900;
  const RECT_TIMEOUT = 1200;

  const adsWithCustomTimeout = ['ad-Rectangle_1', 'ad-Rectangle_2'];

  const ssrRendered = useSelector(selectSsrRendered);

  let prebidTimeout = ssrRendered ? SSR_TIMEOUT : CSR_DEFAULT_TIMEOUT;

  if (adsWithCustomTimeout.includes(id)) {
    prebidTimeout = RECT_TIMEOUT;
  }

  return isDesktop ? DESKTOP_TIMEOUT : prebidTimeout;
};
// AdCore: Component to define, display and refresh AdSlots with given configuration
// only triggered in CSR
const AdCore = memo(({id, sizes, adUnitCode}) => {
  const layout = useLayout();
  const isDesktop = layout !== LayoutMode.Mobile;
  const prebidTimeout = usePrebidTimeout(id, isDesktop);
  useMountAdSlot(adUnitCode, sizes, id, prebidTimeout, isDesktop);

  return <Slot id={id} />;
});

const useMountAdSlot = (adUnitCode, sizes, id, prebidTimeout, isDesktop) => {
  const adsReady = useSelector(selectAdsReady);
  const [disableAds] = useDisableAds();
  const isDesktopRef = useRef(isDesktop);

  useLayoutEffect(() => {
    if (adsReady && !disableAds) {
      loadAdSlot(id, () => defineAndDisplaySlot(adUnitCode, sizes, id, prebidTimeout), isDesktopRef.current);
    }
    return () => {
      destroyOldSlot(adUnitCode);
    };
  }, [adsReady, disableAds, adUnitCode, id, sizes, prebidTimeout]);
};

export {AdCore, selectAdsReady, callWebInterstital};
